<template>
  <div class="funds">
    <div class="funds-title">{{ $t('quickFund') }}</div>
    <form class="funds-form" @submit.prevent="submit">
      <div class="funds-wrap">
        <div class="funds-row">
          <div class="funds-options" v-if="walletTokens.length">
            <div class="funds-options__title">{{ $t('selectCard') }}:</div>
            <Radio checked value="saved" name="saved" class="radio" v-model="optionType">
              {{ $t('useSavedCard') }}
              <p v-for="item in walletTokens" :key="item.id">
                <strong v-if="item.isDefault">(*{{ item.last4 }})</strong>
              </p>
            </Radio>
            <Radio value="new" name="new" class="radio" v-model="optionType">{{ $t('useNewCard') }}</Radio>
          </div>
        </div>
        <div class="funds-row" v-if="optionType === 'new' || !walletTokens.length">
          <div class="card-wrap" v-if="mountStripeElement">
            <card
              class="stripe-card"
              :stripe="$options.stripeApiKey"
              :options="stripeOptions"
              ref="stripeCard"
              :hidePostalCode="true"
              @change="isComplete = $event.complete"
            />
          </div>
        </div>
        <div class="funds-row" v-if="mountStripeElement"></div>
        <div class="funds-row" v-if="mountStripeElement"></div>
        <div class="funds-row" v-if="mountStripeElement"></div>
        <div class="funds-radio">
          <div class="funds-radio__title">{{ $t('selectAmount') }}:</div>
          <Radio v-model="radioValue" checked value="10" name="10$" class="radio" @input="updateCommission()"
            >10$
          </Radio>
          <Radio value="25" @input="updateCommission()" name="25$" class="radio" v-model="radioValue">25$</Radio>
          <Radio value="50" @input="updateCommission()" name="50$" class="radio" v-model="radioValue">50$</Radio>
          <Radio value="100" @input="updateCommission()" name="100$" class="radio" v-model="radioValue">100$</Radio>
          <Radio value="250" @input="updateCommission()" name="250$" class="radio" v-model="radioValue">250$</Radio>
        </div>
        <div class="funds-commission">
          <div class="funds-commission__item">
            <span>XPR {{ $t('funding') }}</span>
            <span>$ {{ radioValue }}</span>
          </div>
          <div class="funds-commission__item">
            <span>{{ $t('serviceCharge') }}</span>
            <span>$ {{ parseFloat(serviceCommission).toFixed(2) }}</span>
          </div>
        </div>
        <div class="funds-action">
          <BaseButton :loading="submitting" :disabled="submitting || isCardDisabled || commissionLoading">
            {{ $t('charge') }}
            <span>${{ parseFloat(totalAmount).toFixed(2) }}</span></BaseButton
          >
        </div>
        <p v-if="errorResp" class="form--error">
          {{ errorResp }}
        </p>
      </div>
    </form>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { Card, createToken } from 'vue-stripe-elements-plus'
import { Radio } from 'vue-checkbox-radio'
import { get } from 'lodash'
import { TransactionType } from '@/store/modules/wallet'

const STRIPE_API_KEY = process.env.VUE_APP_STRIPE_API_KEY
export default {
  name: 'wallet-send-receive',
  data () {
    return {
      optionType: 'saved',
      isComplete: false,
      cardNumber: null,
      loading: false,
      number: false,
      expiry: false,
      cvc: false,
      submitting: false,
      commissionLoading: false,
      errorResp: null,
      radioValue: 10,
      totalAmount: null,
      serviceCommission: 0,
      mountStripeElement: false,
      stripeOptions: {
        hidePostalCode: true,
        classes: {
          base: 'my-card-base-class',
          complete: 'my-card-complete-class',
          empty: 'my-card-empty-class',
          focus: 'my-card-focus-class',
          invalid: 'my-card-invalid-class',
          webkitAutofill: 'my-card-autofill-class'
        },
        style: {
          base: {
            fontSize: '16px',
            color: '#32325d',
            fontFamily: "'Montserrat', sans-serif",
            fontSmoothing: 'antialiased',
            '::placeholder': {
              color: 'rgba(0, 0, 0, 0.4)'
            },
            ':-webkit-autofill': {
              color: '#fce883'
            },
            '-webkit-autofill': {
              color: '#fce883'
            }
          },
          invalid: {
            color: '#fa755a',
            iconColor: '#fa755a'
          }
        }
      }
    }
  },
  stripeApiKey: STRIPE_API_KEY,
  components: {
    Radio,
    Card
  },
  metaInfo () {
    return {
      title: this.$t('titleWallet')
    }
  },
  computed: {
    ...mapGetters({
      walletInfo: 'wallet/walletInfo',
      walletTokens: 'wallet/walletTokens'
    }),
    isCardDisabled () {
      if (!this.walletTokens.length) {
        return !this.isComplete
      } else {
        if (this.optionType === 'saved') {
          return false
        } else {
          return !this.isComplete
        }
      }
    }
  },
  watch: {
    number: 'moveFocus',
    expiry: 'moveFocus',
    cvc: 'moveFocus'
  },
  mounted () {
    this.mountStripeElement = true
  },
  methods: {
    ...mapActions({
      addFunds: 'wallet/addFunds',
      getCommission: 'wallet/getCommission',
      fetchBalance: 'wallet/fetchBalance',
      fetchXpayBalance: 'wallet/fetchXpayBalance'
    }),
    async updateCommission () {
      this.commissionLoading = true
      try {
        const result = await this.getCommission({
          amount: this.radioValue,
          transactionType: TransactionType.REFILL
        })

        this.serviceCommission = result.commissionFee
        this.totalAmount = parseInt(this.radioValue) + parseFloat(this.serviceCommission)
      } finally {
        this.commissionLoading = false
      }
    },
    moveFocus () {
      if (this.number) {
        if (!this.expiry) {
          this.$refs.fundsCardExpiry.focus()
        } else if (!this.cvc) {
          this.$refs.fundsCardCvc.focus()
        }
      } else if (this.expiry) {
        if (!this.cvc) {
          this.$refs.fundsCardCvc.focus()
        } else if (!this.number) {
          this.$refs.fundsCardNumber.focus()
        }
      }
    },
    async submit () {
      this.errorResp = null
      this.submitting = true
      try {
        const data = {
          price: parseFloat(this.radioValue).toFixed(2)
        }
        if (!this.walletTokens.length || (this.walletTokens.length && this.optionType === 'new')) {
          const stripeResp = await createToken()
          if (stripeResp) {
            const {
              token: { id }
            } = stripeResp
            data.chargeToken = id
          }
        }
        await this.addFunds(data)
        await this.fetchBalance()
        const { apiKey, apiSecret } = this.walletInfo
        this.fetchXpayBalance({
          api_key: apiKey,
          secret_key: apiSecret
        })
        this.$modal.show('success')
        this.$router.push({ name: 'wallet-transactions' })
      } catch (error) {
        this.submitting = false
        const stripeError = get(error, 'response.data.error')
        if (stripeError && Object.keys(stripeError).length) {
          const errTitle = Object.keys(stripeError)[0]
          this.errorResp = `${errTitle}: ${stripeError[errTitle].message}`
        } else {
          this.errorResp = this.$t('error')
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.funds {
  padding-right: 15px;
  max-height: 565px;
  overflow: auto;

  &::-webkit-scrollbar {
    width: 8px;
    position: absolute;
    background: rgba(17, 17, 17, 0.1);
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    position: absolute;
    z-index: -2;
  }

  &::-webkit-scrollbar-thumb {
    background: #00b764;
    border-radius: 5px;
  }

  &-title {
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 10px;
  }

  &-action {
    max-width: 300px;
    margin: 20px auto;

    button {
      padding: 15px 30px;
      background: #f7931d;
      width: 100%;

      span {
        font-weight: bold;
      }

      &.button--disabled {
        opacity: 0.5;
      }
    }
  }

  &-radio {
    padding-left: 10px;

    &__title {
      font-size: 14px;
      line-height: 1.2;
      font-weight: 500;
      margin-bottom: 10px;
    }

    & > div {
      margin: 15px 0;
    }
  }

  &-options {
    padding-left: 10px;

    &__title {
      font-size: 14px;
      line-height: 1.2;
      font-weight: 500;
      margin-bottom: 10px;
    }

    .radio {
      padding: 10px;
    }
  }

  &-commission {
    padding: 20px 0;
    display: flex;
    flex-direction: column;
    max-width: 250px;
    margin: 0 auto;

    &__item {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      color: #9c9c9c;
      font-size: 16px;
      line-height: 1.2;

      span:last-child {
        font-weight: bold;
      }

      &:not(:first-child) {
        margin-top: 5px;
      }
    }
  }
}
</style>
<style lang="scss">
.funds-row .radio label {
  display: flex;

  p {
    margin-left: 10px;
  }

  span {
    margin-right: 5px !important;
  }
}

.funds-radio {
  label {
    font-size: 16px;
  }

  .radio-component label span {
    margin-right: 15px;
  }
}

.my-card-base-class {
  border: 1px solid #ccc;
  padding: 10px;
  border-radius: 5px;
}

.my-card-complete-class {
  border-color: #2ecc71;
}

.my-card-empty-class {
  border-color: #ccc;
}

.my-card-focus-class {
  box-shadow: 0 0 0 2px rgba(30, 144, 255, 0.2);
}

.my-card-invalid-class {
  border-color: #fa755a;
}
</style>
